import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearOpenRouterCredential,
  readCanvasAiModel,
  readOpenRouterCredential,
  storeCanvasAiModel,
  storeOpenRouterCredential,
} from "./canvasAi.storage";
import { getCanvasAiErrorMessage } from "./canvasAiErrors";
import {
  fetchOpenRouterModels,
  validateOpenRouterKey,
} from "./openRouter.api";
import { CANVAS_AI_DEFAULT_MODEL_ID } from "./canvasAi.constants";

const EMPTY_CREDENTIAL = Object.freeze({ apiKey: "", isRemembered: false });

const loadOpenRouterAccess = async ({ apiKey, signal }) => {
  await validateOpenRouterKey({ apiKey, signal });
  return fetchOpenRouterModels({ apiKey, signal });
};

const resolveModelId = ({ currentModelId, models }) => {
  if (models.some((model) => model.id === currentModelId)) return currentModelId;
  if (models.some((model) => model.id === CANVAS_AI_DEFAULT_MODEL_ID)) {
    return CANVAS_AI_DEFAULT_MODEL_ID;
  }
  return "";
};

const reconcileModel = ({ models, setModelId }) => {
  setModelId((currentModelId) => {
    const nextModelId = resolveModelId({ currentModelId, models });
    storeCanvasAiModel(nextModelId);
    return nextModelId;
  });
};

/** @returns {object} */
export const useOpenRouterAccess = () => {
  const [credential, setCredential] = useState(readOpenRouterCredential);
  const [error, setError] = useState("");
  const [isCredentialBusy, setCredentialBusy] = useState(false);
  const [isModelsLoading, setModelsLoading] = useState(false);
  const [modelId, setModelId] = useState(readCanvasAiModel);
  const [models, setModels] = useState([]);
  const validatedApiKeyRef = useRef(null);

  useEffect(() => {
    const apiKey = credential.apiKey;
    if (!apiKey || validatedApiKeyRef.current === apiKey) return undefined;
    const controller = new AbortController();
    setModelsLoading(true);
    loadOpenRouterAccess({ apiKey, signal: controller.signal })
      .then((availableModels) => {
        validatedApiKeyRef.current = apiKey;
        setModels(availableModels);
        reconcileModel({ models: availableModels, setModelId });
      })
      .catch((loadError) => {
        if (loadError.name === "AbortError") return;
        setError(getCanvasAiErrorMessage(loadError));
        setModels([]);
      })
      .finally(() => setModelsLoading(false));
    return () => controller.abort();
  }, [credential.apiKey]);

  const connectCredential = useCallback(async ({ apiKey, isRemembered }) => {
    const cleanKey = String(apiKey).trim();
    const controller = new AbortController();
    setCredentialBusy(true);
    setError("");
    try {
      const availableModels = await loadOpenRouterAccess({
        apiKey: cleanKey,
        signal: controller.signal,
      });
      storeOpenRouterCredential({ apiKey: cleanKey, isRemembered });
      validatedApiKeyRef.current = cleanKey;
      setCredential({ apiKey: cleanKey, isRemembered });
      setModels(availableModels);
      reconcileModel({ models: availableModels, setModelId });
      return true;
    } catch (connectError) {
      setError(getCanvasAiErrorMessage(connectError));
      return false;
    } finally {
      setCredentialBusy(false);
    }
  }, []);

  const removeCredential = useCallback(() => {
    clearOpenRouterCredential();
    storeCanvasAiModel("");
    validatedApiKeyRef.current = null;
    setCredential(EMPTY_CREDENTIAL);
    setError("");
    setModelId("");
    setModels([]);
  }, []);

  const selectModel = useCallback((nextModelId) => {
    setModelId(nextModelId);
    storeCanvasAiModel(nextModelId);
  }, []);

  return {
    apiKey: credential.apiKey,
    clearAccessError: () => setError(""),
    connectCredential,
    error,
    hasCredential: Boolean(credential.apiKey),
    isCredentialBusy,
    isModelsLoading,
    isRemembered: credential.isRemembered,
    modelId,
    models,
    removeCredential,
    selectModel,
  };
};
